import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Upload, Gift, Building2, Cpu, Layers, Monitor, Boxes, Wrench, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api, d, errMsg, img, useSeo } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

export function QuoteForm({ kind = "personalizado" }) {
  const [f, setF] = useState({});
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const upload = async (list) => {
    for (const file of list) {
      try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/uploads", fd); setFiles((x) => [...x, r.data]); } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/quotes", { ...f, kind, files }); setSent(true); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (sent) return <div className="card p-10 text-center" data-testid="quote-success"><Sparkles className="h-10 w-10 mx-auto text-[#00C8FF]" /><h3 className="text-2xl font-semibold mt-4">Solicitação enviada!</h3><p className="text-[#A0A0A0] mt-2">Nossa equipe vai analisar e responder em breve pelo WhatsApp ou e-mail.</p></div>;
  return (
    <form onSubmit={submit} className="card p-6 lg:p-8 grid sm:grid-cols-2 gap-4" data-testid="quote-form">
      <div><label className="label">Nome *</label><input required className="input" onChange={up("name")} data-testid="quote-name" /></div>
      <div><label className="label">WhatsApp *</label><input required className="input" inputMode="tel" onChange={up("whatsapp")} data-testid="quote-whatsapp" /></div>
      <div><label className="label">E-mail *</label><input required type="email" className="input" onChange={up("email")} data-testid="quote-email" /></div>
      {kind === "empresa" ? <div><label className="label">Empresa</label><input className="input" onChange={up("company")} data-testid="quote-company" /></div> : <div><label className="label">Quantidade</label><input className="input" inputMode="numeric" onChange={up("quantity")} data-testid="quote-quantity" /></div>}
      {kind === "empresa" && <div><label className="label">Quantidade</label><input className="input" onChange={up("quantity")} data-testid="quote-quantity" /></div>}
      <div><label className="label">Prazo desejado</label><input type="date" className="input" onChange={up("deadline")} data-testid="quote-deadline" /></div>
      <div className="sm:col-span-2"><label className="label">Descrição *</label><textarea required className="input min-h-32" placeholder="Conte sua ideia: tamanho, cores, uso, referências..." onChange={up("description")} data-testid="quote-description" /></div>
      <div className="sm:col-span-2">
        <label className="btn-ghost w-full cursor-pointer border-dashed py-6"><Upload className="h-5 w-5" />Enviar imagens ou arquivos 3D (STL, OBJ, STEP, 3MF, ZIP)<input type="file" multiple className="hidden" onChange={(e) => upload([...e.target.files])} data-testid="quote-files" /></label>
        <div className="flex flex-wrap gap-2 mt-3">{files.map((x, k) => <span key={k} className="chip bg-[#171A1F] border border-[#282D37]">{x.name}<button type="button" aria-label="Remover" onClick={() => setFiles(files.filter((_, i) => i !== k))}><X className="h-3 w-3" /></button></span>)}</div>
      </div>
      <button className="btn-primary sm:col-span-2 py-4" disabled={busy} data-testid="quote-submit">{busy ? "Enviando..." : "SOLICITAR ORÇAMENTO"}</button>
    </form>
  );
}

const Hero = ({ eyebrow, title, text, image }) => (
  <section className="relative overflow-hidden border-b border-[#171A1F]">
    <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#050608] via-[#050608]/90 to-transparent" />
    <div className="relative max-w-[1280px] mx-auto px-4 lg:px-8 py-14 lg:py-24"><p className="eyebrow">{eyebrow}</p><h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 max-w-3xl">{title}</h1><p className="text-[#E6E6E6] mt-5 max-w-xl">{text}</p></div>
  </section>
);

export function CustomPage() {
  useSeo({ title: "Projetos personalizados", description: "Solicite orçamento de peças e projetos personalizados em impressão 3D." });
  return (
    <>
      <Hero eyebrow="Projetos personalizados" title="Sua ideia, impressa em 3D." text="Peças sob medida, presentes únicos, reposições e protótipos. Envie seu arquivo ou descreva a ideia." image="/demo/printer.jpg" />
      <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-16 grid lg:grid-cols-[1fr_1.4fr] gap-12">
        <ol className="space-y-6">{["Envie a ideia ou o arquivo 3D", "Analisamos e enviamos o orçamento", "Aprovou? Produzimos camada por camada", "Entregamos para todo o Brasil"].map((s, k) => <li key={s} className="flex gap-4 items-center"><span className="font-mono h-11 w-11 rounded-xl grid place-items-center border border-[#008CFF]/40 text-[#00C8FF] shrink-0">0{k + 1}</span><span className="font-semibold">{s}</span></li>)}</ol>
        <QuoteForm />
      </div>
    </>
  );
}

const B2B = [[Gift, "Brindes"], [Building2, "Produtos com logo"], [Cpu, "Prototipagem"], [Layers, "Pequenas séries"], [Monitor, "Displays"], [Boxes, "Organizadores"], [Wrench, "Peças técnicas"], [Sparkles, "Projetos personalizados"]];
export function CompaniesPage() {
  useSeo({ title: "Empresas", description: "Brindes, prototipagem e pequenas séries em impressão 3D para empresas." });
  return (
    <>
      <Hero eyebrow="E³ para empresas" title="Impressão 3D para sua marca." text="Brindes corporativos, displays, protótipos e pequenas séries com qualidade e prazo." image="/demo/farm.jpg" />
      <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-16">{B2B.map(([I, t]) => <div key={t} className="card p-6"><I className="h-6 w-6 text-[#00C8FF]" /><div className="font-semibold mt-4">{t}</div></div>)}</div>
        <h2 className="text-3xl font-semibold mb-6">Solicitar orçamento</h2>
        <QuoteForm kind="empresa" />
      </div>
    </>
  );
}

const POLICY_TITLES = { privacidade: "Política de Privacidade", termos: "Termos de Uso", trocas: "Política de Troca", entrega: "Política de Entrega", personalizados: "Política de Personalizados", cookies: "Política de Cookies", sobre: "Sobre a E³" };
export function PolicyPage() {
  const { slug } = useParams();
  const { settings } = useStore();
  const title = POLICY_TITLES[slug] || "Política";
  useSeo({ title });
  const html = settings?.policies?.[slug];
  return (
    <div className="max-w-3xl mx-auto px-4 py-16">
      <h1 className="text-4xl font-bold">{title}</h1>
      {html ? <div className="mt-8 text-[#E6E6E6] leading-relaxed whitespace-pre-line" data-testid="policy-content">{html}</div>
        : <p className="mt-8 text-[#A0A0A0]" data-testid="policy-placeholder">[Conteúdo a ser cadastrado em Administrativo › Configurações › Políticas]</p>}
    </div>
  );
}

const BLOG_CATS = ["Impressão 3D", "Gamer", "Decoração", "Dicas", "Tecnologia", "Projetos"];
export function BlogList() {
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("");
  useSeo({ title: "Blog" });
  useEffect(() => { api.get("/blog", { params: { category: cat } }).then((r) => setPosts(r.data)); }, [cat]);
  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-12">
      <h1 className="text-4xl font-bold">Blog E³</h1>
      <div className="flex gap-2 mt-6 overflow-x-auto scrollbar-none">{["", ...BLOG_CATS].map((c) => <button key={c} onClick={() => setCat(c)} className={`chip px-4 py-2 border ${cat === c ? "border-[#008CFF] text-[#00C8FF]" : "border-[#282D37]"}`}>{c || "Todos"}</button>)}</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-testid="blog-list">
        {posts.map((p) => <Link key={p.id} to={`/blog/${p.slug}`} className="card overflow-hidden group"><img src={img(p.cover)} alt={p.title} loading="lazy" className="aspect-video w-full object-cover group-hover:opacity-80" /><div className="p-5"><span className="eyebrow">{p.category}</span><h2 className="font-semibold text-lg mt-2">{p.title}</h2><p className="text-sm text-[#A0A0A0] mt-2">{p.excerpt}</p></div></Link>)}
        {!posts.length && <p className="text-[#A0A0A0]">Nenhum post publicado.</p>}
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [slug]);
  useSeo({ title: p?.title, description: p?.excerpt, image: p?.cover, jsonLd: p && { "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title, datePublished: p.created_at } });
  if (p === false) return <div className="py-32 text-center">Post não encontrado</div>;
  if (!p) return <div className="py-32" />;
  return (
    <article className="max-w-3xl mx-auto px-4 py-12">
      <span className="eyebrow">{p.category}</span><h1 className="text-4xl lg:text-5xl font-bold mt-3">{p.title}</h1><p className="text-sm text-[#A0A0A0] mt-3">{d(p.created_at)}</p>
      {p.cover && <img src={img(p.cover)} alt={p.title} className="rounded-2xl mt-8 w-full aspect-video object-cover" />}
      <div className="mt-8 text-[#E6E6E6] leading-relaxed space-y-4" dangerouslySetInnerHTML={{ __html: (p.content || "").replace(/<script[\s\S]*?<\/script>/gi, "").replace(/ on\w+="[^"]*"/gi, "") }} />
    </article>
  );
}
